import { Button } from "primereact/button";
import { useNavigate } from "react-router-dom";
import { Dialog } from "primereact/dialog";
import { Ripple } from "primereact/ripple";
import React, { useEffect, useRef, useState } from "react";
import { useAppSelector, useAppDispatch } from "../../../Controllers/hooks";
import { InputText } from "primereact/inputtext";
import { setCheckedProjectContactForAssign, setContactSearchKey, setSelectedUserType } from "../../../Controllers/Slices/projectSlice";
import { Checkbox } from "primereact/checkbox";
import { Image } from "primereact/image";
import { ProjectContact } from "../../../Controllers/Types/Project/ProjectContact";

interface AssignEmployeeModalProps {
    isOpen: boolean;
    onAssign: () => void;
    onClose: () => void;
    loading: boolean;
}

const AssignEmployeeModal: React.FC<AssignEmployeeModalProps> = ({
    isOpen,
    onAssign,
    onClose,
    loading
}) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [bodyHeight, setBodyHeight] = useState<number | "auto">("auto");
    const contactListBodyRef = useRef<HTMLDivElement>(null);
    const [searchKey, setSearchKey] = useState<string>('');

    /* State values */
    const projectContacts = useAppSelector((state) => state.project.projectContacts);
    const projectContactsFiltered = useAppSelector((state) => state.project.projectContactsFiltered);

    const userType = useAppSelector((state) => state.project.selectedUserType);
    const countAll = useAppSelector((state) => state.project.countAll);
    const countEmployee = useAppSelector((state) => state.project.countEmployee);
    const countProfessional = useAppSelector((state) => state.project.countProfessional);
    const countClient = useAppSelector((state) => state.project.countClient);


    const updateHeight = () => {
        if (contactListBodyRef.current) {
            setBodyHeight(contactListBodyRef.current.offsetHeight - 170);
        }
    };

    useEffect(() => {
        if (isOpen && contactListBodyRef.current) {
            updateHeight();

            window.addEventListener("resize", updateHeight);

            return () => {
                window.removeEventListener("resize", updateHeight);
            };
        }
    }, [isOpen, contactListBodyRef.current]);

    /* Modal header */
    const assignContactModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">Add project contact</h5>
            <button
                type="button"
                className="close_modal_btn p-ripple"
                onClick={onClose}
            >
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    /* Modal footer */
    const assignContactModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Close"
                className="custom_btn secondary"
                onClick={onClose}
            />

            <Button
                label={`${loading ? "Assigning" : "Assign"}`}
                loading={loading}
                onClick={onAssign}
                className="custom_btn primary"
            />
        </div>
    );

    const handleSelectUserType = (type: "Employee" | "Professional" | "Client" | null) => {
        dispatch(setSelectedUserType(type));
        dispatch(setContactSearchKey(''));
        setSearchKey('')
    };

    const handleContactSearchKey = (e: string) => {
        setSearchKey(e)
        dispatch(setContactSearchKey(e))
    };

    const handleCheckForAssignContact = (projectContract: ProjectContact) => {
        dispatch(setCheckedProjectContactForAssign(projectContract));
    };

    return (
        <Dialog
            visible={isOpen}
            header={assignContactModalHeader}
            footer={assignContactModalFooter}
            headerClassName="custom_modal_header"
            className="custom_modal_dialog modal_dialog_sm assign_contact_modal"
            onHide={onClose}
            dismissableMask
        >
            <div className="custom_modal_body fixed_height" ref={contactListBodyRef}>
                <div className="pt-0 px-sm-0 px-1 pb-2 w-100">
                    <div className="share_contact_tab_header">
                        <div className="contact_tabs">
                            <div className="contact_tab_item">
                                <button
                                    type="button"
                                    className={`contact_tab_btn ${userType === null ? "active" : ""} p-ripple`}
                                    onClick={() => handleSelectUserType(null)}
                                >
                                    {`All: ${String(countAll).padStart(2, '0')}`}
                                    <Ripple pt={{ root: { style: { background: "rgba(164, 96, 253, 0.2)" } } }} />
                                </button>
                            </div>

                            <div className="contact_tab_item">
                                <button
                                    type="button"
                                    className={`contact_tab_btn ${userType === "Employee" ? "active" : ""} p-ripple`}
                                    onClick={() => handleSelectUserType("Employee")}
                                >
                                    {`Employee: ${String(countEmployee).padStart(2, '0')}`}
                                    <Ripple pt={{ root: { style: { background: "rgba(164, 96, 253, 0.2)" } } }} />
                                </button>
                            </div>

                            <div className="contact_tab_item">
                                <button
                                    type="button"
                                    className={`contact_tab_btn ${userType === "Professional" ? "active" : ""} p-ripple`}
                                    onClick={() => handleSelectUserType("Professional")}
                                >
                                    {`Professional: ${String(countProfessional).padStart(2, '0')}`}
                                    <Ripple pt={{ root: { style: { background: "rgba(164, 96, 253, 0.2)" } } }} />
                                </button>
                            </div>

                            <div className="contact_tab_item">
                                <button
                                    type="button"
                                    className={`contact_tab_btn ${userType === "Client" ? "active" : ""} p-ripple`}
                                    onClick={() => handleSelectUserType("Client")}
                                >
                                    {`Client: ${String(countClient).padStart(2, '0')}`}
                                    <Ripple pt={{ root: { style: { background: "rgba(164, 96, 253, 0.2)" } } }} />
                                </button>
                            </div>
                        </div>

                        <div className="data_search_input_area contact_search">
                            <InputText
                                key={"contactSearchKey"}
                                id="contactSearchKey"
                                value={searchKey}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    handleContactSearchKey(e.target.value)
                                }
                                className="custom_form_input data_search_input"
                                placeholder="Search contact by name"
                                autoComplete="off"
                                autoFocus
                                type={"search"}
                            />
                            {searchKey === "" ? (
                                <i className="bi bi-search"></i>
                            ) : (
                                <i
                                    className="bi bi-x-lg data_search_input_clear"
                                    onClick={() => handleContactSearchKey('')}
                                ></i>
                            )}
                        </div>
                    </div>

                    {/* <div className="data_view_filter_area">
                        <div className="row">
                          <div className="col-12 col-xl-6 col-lg-6 col-sm-6 ms-auto me-0">
                            <div className="page_form_group mb-3 mb-sm-4">
                              <Dropdown value={userType} id="userType" onChange={handleSelectUserType}
                                options={userTypes} valueTemplate={selectedUserTypeTemplate} itemTemplate={userTypeOptionTemplate}
                                placeholder="Select user type" className="form_dropdown w-100" showClear />
                            </div>
                          </div>
        
                          <div className="col-12 col-xl-6 col-lg-6 col-sm-6 ms-0 me-auto">
                            <div className="page_form_group mb-4">
                              <Dropdown value={role} id="role" onChange={handleSelectUserRole}
                                options={userRoles} optionLabel="role_name" valueTemplate={selectedRoleTemplate} itemTemplate={roleOptionTemplate}
                                placeholder="Select role" className="form_dropdown w-100" showClear />
                            </div>
                          </div>
                        </div>
                      </div> */}

                    <div className="row">
                        {projectContactsFiltered?.length > 0 ? (
                            <div className="col-12">
                                <ul className="assign_contact_list_area mt-sm-2 ps-sm-4 pe-sm-4 mb-sm-3 mb-2">
                                    {projectContactsFiltered.map((contact) => (
                                        <li className="assign_contact_list w-100">
                                            <Checkbox
                                                onChange={(e) => handleCheckForAssignContact(contact)}
                                                checked={contact.icChecked}
                                            ></Checkbox>

                                            <button
                                                className={`contact_slide_tab assign_contact p-ripple ${contact.icChecked && "active"}`}
                                                onClick={(e) => handleCheckForAssignContact(contact)}
                                            >
                                                <Image
                                                    className="contact_profile_img"
                                                    src={contact?.profile_image}
                                                    alt="Profile"
                                                />
                                                <div className="contact_slide_det">
                                                    <h6>
                                                        {contact?.first_name + " " + contact?.last_name}
                                                    </h6>

                                                    {contact.role_name && (
                                                        <p>{contact.role_name}</p>
                                                    )}

                                                    {contact?.email && (
                                                        <small>
                                                            {contact?.email}
                                                        </small>
                                                    )}
                                                </div>
                                                <Ripple pt={{ root: { style: { background: "rgba(164, 96, 253, 0.2)" } } }} />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ) : (
                            <div className="col-12">
                                <div className="no_data_found d-flex align-content-center flex-column justify-content-center mt-2 mb-2"
                                    style={{ height: bodyHeight }}>
                                    <h6>
                                        {searchKey
                                            ? `No contact(s) found matching "${searchKey}".`
                                            : (userType === "All" || projectContacts?.length === 0)
                                                ? "No contacts have been created yet. Go to Contacts to create, then assign contacts to the project."
                                                : `No ${userType?.toLowerCase()} contact(s) found!`}
                                    </h6>

                                    {projectContacts?.length === 0 && (
                                        <button
                                            className={`add_data_btn p-ripple mt-3 mx-auto`}
                                            onClick={() => navigate('/contact')}
                                        >
                                            <i className={`bi bi-box-arrow-up-left`}></i>
                                            Contacts
                                            <Ripple />
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Dialog>
    )
}


export default AssignEmployeeModal;